import React, { useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
  ConnectNotificationBanner,
  ConnectAccountManagement,
  ConnectDocuments,
} from '@stripe/react-connect-js';
import { StripePublishableKey } from '../../../appConfig';
import { RootStoreContext } from '../../../global/storeContext';

export const StripeAccountComponent: React.FC<{
  accountId: string;
}> = observer(({ accountId }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { bankAccountStore, userStore } = useContext(RootStoreContext);
  const [bankAccountStatus, setBankAccountStatus] = useState<{
    details_submitted: boolean;
    charges_enabled: boolean;
    payouts_enabled: boolean;
  }>({ details_submitted: false, charges_enabled: false, payouts_enabled: false });

  const stripeConnectInstanceRef = useRef<StripeConnectInstance | null>(null);

  if (!stripeConnectInstanceRef.current) {
    const fetchClientSecret = async () => {
      const acctSession = await bankAccountStore.createStripeAccountSession(accountId);

      if (!acctSession) {
        const error = 'Failed to create Stripe Account Session.';
        console.error('An error occurred: Failed to create Stripe Account Session.', error);
        setErrorMessage('An error has occured.');
        return '';
      } else {
        return acctSession;
      }
    };

    stripeConnectInstanceRef.current = loadConnectAndInitialize({
      publishableKey: StripePublishableKey ?? '',
      fetchClientSecret: fetchClientSecret,
    });
  }

  const stripeConnectInstance = stripeConnectInstanceRef.current;

  const fetchBankAccountStatus = async () => {
    try {
      if (!userStore.userId) {
        throw new Error('Not Authenticated');
      }

      const status = await bankAccountStore.bankAccountSetupStatusForUser(userStore.userId);
      if (status) {
        setBankAccountStatus({
          details_submitted: status.details_submitted,
          charges_enabled: status.charges_enabled,
          payouts_enabled: status.payouts_enabled,
        });
      }
    } catch (error) {
      console.error('Error fetching bank account setup status:', error);
      setErrorMessage('Failed to fetch bank account setup status.');
    }
  };

  useEffect(() => {
    fetchBankAccountStatus();
  }, [accountId]);

  return (
    <>
      {errorMessage ? (
        <div>... error ... </div>
      ) : (
        <div style={{ paddingBottom: '30px' }}>
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectNotificationBanner
              onLoadError={(err) => {
                console.error(
                  'An error occurred during ConnectNotificationBanner load:',
                  err,
                );
              }}
              collectionOptions={{
                futureRequirements: 'include',
                fields: 'eventually_due',
              }}
              onNotificationsChange={(notifications) => {
                console.log('Notifications changed:', notifications);
              }}
            />

            {!bankAccountStatus.details_submitted && (
              <ConnectAccountOnboarding
                onExit={() => {
                  console.log('The account has exited onboarding');
                  fetchBankAccountStatus();
                }}
                onLoadError={(err) => {
                  console.error('An error occurred during onboarding load:', err);
                  setErrorMessage(
                    'An error occurred while loading the onboarding. Please try again later.',
                  );
                }}
                collectionOptions={{
                  futureRequirements: 'include',
                  fields: 'eventually_due',
                }}
                onStepChange={(step) => {
                  console.log('Onboarding step changed to:', step);
                }}
              />
            )}

            {bankAccountStatus.details_submitted && (
              <>
                <ConnectDocuments
                  onLoadError={(err) => {
                    console.error('An error occurred during ConnectDocuments load:', err);
                  }}
                />
                <ConnectAccountManagement
                  onLoadError={(err) => {
                    console.error(
                      'An error occurred during ConnectAccountManagement load:',
                      err,
                    );
                  }}
                />
              </>
            )}
          </ConnectComponentsProvider>
        </div>
      )}
    </>
  );
});
